import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Switch,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { useState } from 'react';
import { doctrineApi, type DoctrineUpdateRequestDto } from '../api/admin';

export function DoctrineDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const doctrine = useQuery({
    queryKey: ['doctrine', id],
    queryFn: () => doctrineApi.get(id),
  });
  const steps = useQuery({
    queryKey: ['doctrine-steps', id],
    queryFn: () => doctrineApi.listSteps(id),
  });

  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [specJson, setSpecJson] = useState<string | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (displayName !== null) patch.displayName = displayName;
      if (isActive !== null) patch.isActive = isActive;
      if (specJson !== null) {
        try {
          patch.spec = JSON.parse(specJson);
          setSpecError(null);
        } catch (e) {
          setSpecError(e instanceof Error ? e.message : String(e));
          throw e;
        }
      }
      return doctrineApi.update(id, patch as DoctrineUpdateRequestDto);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctrine', id] });
      qc.invalidateQueries({ queryKey: ['doctrines'] });
    },
  });

  if (doctrine.isLoading) return <Typography>Loading…</Typography>;
  if (!doctrine.data) return <Typography>Not found.</Typography>;

  const liveDisplayName = displayName ?? doctrine.data.displayName;
  const liveIsActive = isActive ?? doctrine.data.isActive;
  const liveSpecJson =
    specJson ?? JSON.stringify(doctrine.data.spec ?? {}, null, 2);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {doctrine.data.displayName}
        </Typography>
        <Chip size="small" label={doctrine.data.id} />
        <Chip size="small" label={`v${doctrine.data.version}`} />
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Doctrine
          </Typography>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Display name"
              value={liveDisplayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={liveIsActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Spec (JSON)
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', mt: 0.5 }}>
                <Editor
                  height="280px"
                  language="json"
                  value={liveSpecJson}
                  onChange={(v) => setSpecJson(v ?? '')}
                  options={{ minimap: { enabled: false }, fontSize: 12 }}
                />
              </Box>
              {specError && (
                <Typography variant="caption" color="error">
                  {specError}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => update.mutate()}
                disabled={update.isPending}
              >
                Save
              </Button>
              {update.isError && (
                <Typography variant="caption" color="error" sx={{ alignSelf: 'center' }}>
                  Save failed.
                </Typography>
              )}
              {update.isSuccess && (
                <Typography variant="caption" color="success.main" sx={{ alignSelf: 'center' }}>
                  Saved.
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Steps ({steps.data?.length ?? 0})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ordinal</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Capability</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Emits milestone</TableCell>
                <TableCell>Timeout (ms)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(steps.data ?? []).map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.ordinal}</TableCell>
                  <TableCell>{s.id}</TableCell>
                  <TableCell>{s.capability}</TableCell>
                  <TableCell>{s.required ? 'yes' : 'no'}</TableCell>
                  <TableCell>{s.emitsMilestone ?? '—'}</TableCell>
                  <TableCell>{s.timeoutMs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
